import React, { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { toast } from '@/components/ui/use-toast';
import { formatCurrency } from '@/lib/app-utils';
import { Download, Plus, FileText, Search, Filter, TrendingUp, TrendingDown, Check, X, Trash2, Wallet, Eye, Pencil } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import FileUpload from '@/components/ui/FileUpload';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { overheadAPI } from '@/services/overhead';

const CATEGORIES = [
  'Office Rent/Utilities',
  'Salaries',
  'Transport/Fuel',
  'Marketing',
  'Legal/Visa',
  'Other',
];

const OverheadPage = () => {
  const { user: currentUser } = useAuth();
  const [expenses, setExpenses]               = useState([]);
  const [loading, setLoading]                 = useState(true);
  const [filterCategory, setFilterCategory]   = useState('All');
  const [filterStatus, setFilterStatus]       = useState('All');

  // Analytics
  const [selectedYear, setSelectedYear]   = useState(new Date().getFullYear().toString());
  const [selectedMonth, setSelectedMonth] = useState('All');
  const [statsData, setStatsData]         = useState(null);

  // New Expense
  const [title, setTitle]             = useState('');
  const [amount, setAmount]           = useState('');
  const [date, setDate]               = useState('');
  const [category, setCategory]       = useState('Office Rent/Utilities');
  const [vatIncluded, setVatIncluded] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [submitting, setSubmitting]   = useState(false);

  // Files Viewer
  const [viewingFiles, setViewingFiles]               = useState(false);
  const [currentExpenseFiles, setCurrentExpenseFiles] = useState([]);
  const [loadingFiles, setLoadingFiles]               = useState(false);
  const [currentExpenseTitle, setCurrentExpenseTitle] = useState('');
  const [previewFile, setPreviewFile]                 = useState(null);

  // ── Edit dialog ────────────────────────────────────────────────────────────
  const [editOpen, setEditOpen]               = useState(false);
  const [editExpense, setEditExpense]         = useState(null);
  const [editTitle, setEditTitle]             = useState('');
  const [editAmount, setEditAmount]           = useState('');
  const [editDate, setEditDate]               = useState('');
  const [editCategory, setEditCategory]       = useState('Office Rent/Utilities');
  const [editVat, setEditVat]                 = useState(false);
  const [editNotes, setEditNotes]             = useState('');
  const [editSubmitting, setEditSubmitting]   = useState(false);

  // Files inside edit dialog
  const [editCurrentFiles, setEditCurrentFiles]       = useState([]);   // already uploaded
  const [editLoadingFiles, setEditLoadingFiles]       = useState(false);
  const [editFilesToDelete, setEditFilesToDelete]     = useState([]);   // ids to delete on save
  const [editNewFiles, setEditNewFiles]               = useState([]);   // new files to upload

  const monthOptions = [
    { value: 'All', label: 'All Months'  },
    { value: '1',   label: 'January'     },
    { value: '2',   label: 'February'    },
    { value: '3',   label: 'March'       },
    { value: '4',   label: 'April'       },
    { value: '5',   label: 'May'         },
    { value: '6',   label: 'June'        },
    { value: '7',   label: 'July'        },
    { value: '8',   label: 'August'      },
    { value: '9',   label: 'September'   },
    { value: '10',  label: 'October'     },
    { value: '11',  label: 'November'    },
    { value: '12',  label: 'December'    },
  ];

  useEffect(() => { loadExpenses(); }, []);
  useEffect(() => { loadStats(selectedYear); }, [selectedYear]);

  const loadExpenses = async () => {
    try {
      setLoading(true);
      const response = await overheadAPI.getExpenses();
      if (response.success) setExpenses(response.data || []);
    } catch (error) {
      console.error('Failed to load expenses', error);
      toast({ title: 'Error', description: 'Failed to load expenses data', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async (year) => {
    try {
      const response = await overheadAPI.getStats(year);
      if (response.success) setStatsData(response.data);
    } catch (error) {
      console.error('Failed to load stats', error);
    }
  };

  const calculateFinalAmount = () => {
    const base = parseFloat(amount) || 0;
    return vatIncluded ? base * 1.05 : base;
  };
  const finalAmount = calculateFinalAmount();

  // ── Open edit dialog & load its files ─────────────────────────────────────
  const handleOpenEdit = async (exp) => {
    setEditExpense(exp);
    setEditTitle(exp.description || exp.title || '');
    setEditAmount(
      exp.vat_included == 1 && exp.amount_before_vat
        ? String(exp.amount_before_vat)
        : String(exp.amount)
    );
    setEditDate(exp.date ? exp.date.split('T')[0] : '');
    setEditCategory(exp.category || 'Office Rent/Utilities');
    setEditVat(exp.vat_included == 1);
    setEditNotes(exp.notes || '');
    setEditFilesToDelete([]);
    setEditNewFiles([]);
    setEditOpen(true);

    // Load existing files
    try {
      setEditLoadingFiles(true);
      const res = await overheadAPI.getFiles(exp.id);
      if (res.success) setEditCurrentFiles(res.data || []);
    } catch {
      setEditCurrentFiles([]);
    } finally {
      setEditLoadingFiles(false);
    }
  };

  const editFinalAmount = (() => {
    const base = parseFloat(editAmount) || 0;
    return editVat ? base * 1.05 : base;
  })();

  // Mark a currently-uploaded file for deletion (just visual until Save)
  const handleMarkFileForDelete = (fileId) => {
    setEditFilesToDelete(prev =>
      prev.includes(fileId) ? prev.filter(id => id !== fileId) : [...prev, fileId]
    );
  };

  // Remove a newly-added (not yet uploaded) file
  const handleRemoveNewFile = (index) => {
    setEditNewFiles(prev => prev.filter((_, i) => i !== index));
  };

  // ── Submit edit ────────────────────────────────────────────────────────────
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editExpense) return;

    try {
      setEditSubmitting(true);

      const payload = {
        id:            editExpense.id,
        title:         editTitle,
        amount:        parseFloat(editAmount),
        vatIncluded:   editVat,
        date:          editDate,
        category:      editCategory,
        notes:         editNotes,
        deleteFileIds: editFilesToDelete,   // ← backend will delete these
      };

      const response = await overheadAPI.updateExpense(payload);

      if (response.success) {
        // Upload new files if any
        if (editNewFiles.length > 0) {
          await Promise.all(editNewFiles.map(f => overheadAPI.uploadFile(editExpense.id, f)));
        }

        await new Promise(r => setTimeout(r, 300));
        await loadExpenses();
        setEditOpen(false);
        toast({ title: 'Expense Updated', description: 'Changes saved. Status reset to Pending for re-approval.' });
      }
    } catch (error) {
      console.error(error);
      toast({ title: 'Error', description: 'Failed to update expense.', variant: 'destructive' });
    } finally {
      setEditSubmitting(false);
    }
  };

  // ── Files viewer (view button in list) ────────────────────────────────────
  const handleViewFiles = async (expenseId, expenseTitle) => {
    try {
      setLoadingFiles(true);
      setViewingFiles(true);
      setPreviewFile(null);
      setCurrentExpenseTitle(expenseTitle);
      const response = await overheadAPI.getFiles(expenseId);
      if (response.success) setCurrentExpenseFiles(response.data || []);
    } catch {
      toast({ title: 'Error', description: 'Failed to load files', variant: 'destructive' });
    } finally {
      setLoadingFiles(false);
    }
  };

  const handleDownloadFile = (fileId, filename) => {
    const url = `${import.meta.env.VITE_API_BASE_URL}/overhead/download-file.php?id=${fileId}`;
    window.open(url, '_blank');
    toast({ title: 'Downloading', description: filename });
  };

  // Build a preview URL that sends ?preview=1 so the server sends inline headers
  const buildPreviewUrl = (fileId) =>
    `${import.meta.env.VITE_API_BASE_URL}/overhead/download-file.php?id=${fileId}&preview=1`;

  const isImage = (file) =>
    (file.mime_type && file.mime_type.startsWith('image/')) ||
    /\.(jpg|jpeg|png|gif|webp)$/i.test(file.original_filename);

  const isPdf = (file) =>
    (file.mime_type && file.mime_type === 'application/pdf') ||
    /\.pdf$/i.test(file.original_filename);

  // ── Multiple file attach (new expense) ────────────────────────────────────
  const handleFilesAttached = (files) => {
    setAttachedFiles(prev => [...prev, ...files]);
    toast({ title: 'Files Added', description: `${files.length} file${files.length > 1 ? 's' : ''} ready for upload.` });
  };

  const handleRemoveAttachedFile = (index) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // ── Submit new expense ─────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);

      const expenseData = {
        title,
        amount: parseFloat(amount),
        vatIncluded,
        date,
        category,
        status: 'Pending',
        createdBy: currentUser.id,
      };

      const response = await overheadAPI.createExpense(expenseData);

      if (response.success) {
        const newExpenseId = response.data.id;

        if (attachedFiles.length > 0) {
          await Promise.all(attachedFiles.map(file => overheadAPI.uploadFile(newExpenseId, file)));
        }

        await new Promise(r => setTimeout(r, 400));
        await loadExpenses();

        setTitle(''); setAmount(''); setDate('');
        setCategory('Office Rent/Utilities');
        setVatIncluded(false); setAttachedFiles([]);

        toast({ title: 'Expense Added', description: 'Overhead cost recorded successfully.' });
      }
    } catch (error) {
      console.error(error);
      toast({ title: 'Error', description: 'Failed to process expense.', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  // ── Approve / Reject / Delete ──────────────────────────────────────────────
  const handleApprove = async (id) => {
    try {
      const response = await overheadAPI.approveExpense(id, 'Approved');
      if (response.success) {
        await new Promise(r => setTimeout(r, 400));
        await loadExpenses();
        toast({ title: 'Expense approved successfully', className: 'bg-green-50 border-green-200 text-green-800' });
      }
    } catch {
      toast({ title: 'Error', description: 'Failed to approve expense', variant: 'destructive' });
    }
  };

  const handleReject = async (id) => {
    try {
      const response = await overheadAPI.approveExpense(id, 'Rejected');
      if (response.success) {
        await new Promise(r => setTimeout(r, 400));
        await loadExpenses();
        toast({ title: 'Expense rejected', variant: 'destructive' });
      }
    } catch {
      toast({ title: 'Error', description: 'Failed to reject expense', variant: 'destructive' });
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await overheadAPI.deleteExpense(id);
      if (response.success) {
        await new Promise(r => setTimeout(r, 400));
        await loadExpenses();
        toast({ title: 'Expense deleted successfully' });
      }
    } catch {
      toast({ title: 'Error', description: 'Failed to delete expense', variant: 'destructive' });
    }
  };

  const handleCancel = async (id) => {
    await handleDelete(id);
    toast({ title: 'Expense cancelled', description: 'The pending expense has been removed.' });
  };

  const isAdmin = currentUser?.role === 'Admin';

  // ── Export CSV ─────────────────────────────────────────────────────────────
  const exportCSV = () => {
    const headers = ['Title', 'Category', 'Amount', 'VAT Included', 'Date', 'Status', 'Approved By'];
    const csvContent = [
      headers.join(','),
      ...expenses.map(e =>
        `"${e.description || e.title}","${e.category}","${e.amount}","${e.vat_included ? 'Yes' : 'No'}","${e.date}","${e.status || 'Pending'}","${e.approved_by || ''}"`
      ),
    ].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'overhead_costs.csv';
    link.click();
  };

  // ── Filters & totals ───────────────────────────────────────────────────────
  const filteredExpenses = useMemo(() => expenses.filter(e => {
    if (!e) return false;
    const categoryMatch = filterCategory === 'All' || e.category === filterCategory;
    const statusMatch   = filterStatus   === 'All' || (e.status || 'Pending') === filterStatus;
    return categoryMatch && statusMatch;
  }), [expenses, filterCategory, filterStatus]);

  const filteredTotal = useMemo(() =>
    filteredExpenses
      .filter(e => e.status === 'Approved')
      .reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0),
  [filteredExpenses]);

  const availableYears = useMemo(() => {
    const years = new Set([new Date().getFullYear()]);
    expenses.forEach(e => { const d = new Date(e.date); if (!isNaN(d)) years.add(d.getFullYear()); });
    return Array.from(years).sort((a, b) => b - a);
  }, [expenses]);

  const analyticsData = useMemo(() => {
    if (!statsData) return {
      monthlyData: [], yearTotal: 0, avgMonthly: 0,
      highestMonth: { name: '-', total: 0 }, lowestMonth: { name: '-', total: 0 },
      maxTotal: 100, currentYear: parseInt(selectedYear), selectedMonthData: null,
    };

    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const monthlyData = months.map((name, idx) => {
      const m = statsData.monthly?.find(m => m.month === idx + 1) || { total: 0, count: 0 };
      return { name, monthNumber: idx + 1, total: parseFloat(m.total) || 0, count: m.count || 0 };
    });

    let displayData = monthlyData;
    let selectedMonthData = null;
    if (selectedMonth !== 'All') {
      selectedMonthData = monthlyData.find(m => m.monthNumber === parseInt(selectedMonth));
      displayData = selectedMonthData ? [selectedMonthData] : [];
    }

    const yearTotal    = parseFloat(statsData.yearTotal)  || 0;
    const avgMonthly   = parseFloat(statsData.avgMonthly) || 0;
    const sorted       = [...monthlyData].sort((a, b) => b.total - a.total);
    const highestMonth = sorted[0];
    const lowestMonth  = sorted[monthlyData.filter(m => m.total > 0).length - 1] || sorted[11];
    const maxTotal     = Math.max(...displayData.map(d => d.total), 100);

    return { monthlyData: displayData, allMonthsData: monthlyData, yearTotal, avgMonthly, highestMonth, lowestMonth, maxTotal, currentYear: parseInt(selectedYear), selectedMonthData };
  }, [statsData, selectedYear, selectedMonth]);

  const getStatusBadge = (status) => {
    switch (status) {
      case 'Approved': return <Badge className="bg-green-100 text-green-700 hover:bg-green-200 border-none">Approved</Badge>;
      case 'Rejected': return <Badge className="bg-red-100 text-red-700 hover:bg-red-200 border-none">Rejected</Badge>;
      default:         return <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-200 border-none">Pending</Badge>;
    }
  };

  // ── Reusable file preview panel (blob fetch — sends session cookies) ────────
  const FilePreviewPanel = ({ file, onClose }) => {
    const [blobUrl, setBlobUrl]       = useState(null);
    const [fetchError, setFetchError] = useState(false);
    const [fetching, setFetching]     = useState(false);

    useEffect(() => {
      if (!file) return;
      let objectUrl = null;
      setFetchError(false);
      setBlobUrl(null);

      const load = async () => {
        try {
          setFetching(true);
          const res = await fetch(buildPreviewUrl(file.id), { credentials: 'include' });
          if (!res.ok) throw new Error('fetch failed');
          const blob = await res.blob();
          objectUrl = URL.createObjectURL(blob);
          setBlobUrl(objectUrl);
        } catch {
          setFetchError(true);
        } finally {
          setFetching(false);
        }
      };

      load();
      return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
    }, [file?.id]);

    if (!file) return null;

    return (
      <div className="mt-4 border rounded-lg overflow-hidden bg-slate-100">
        <div className="flex items-center justify-between px-3 py-2 bg-slate-200 text-xs font-medium text-stone-600">
          <span className="truncate">{file.original_filename}</span>
          <button onClick={onClose} className="text-stone-400 hover:text-red-500 ml-2">
            <X className="w-4 h-4" />
          </button>
        </div>

        {fetching ? (
          <div className="flex justify-center items-center h-[200px]">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-safety-orange" />
          </div>
        ) : fetchError ? (
          <div className="flex flex-col items-center justify-center py-10 text-stone-500 gap-3">
            <FileText className="w-10 h-10 text-stone-300" />
            <p className="text-sm">Could not load preview.</p>
            <Button size="sm" onClick={() => handleDownloadFile(file.id, file.original_filename)}
              className="bg-navy-steal text-white">
              <Download className="w-4 h-4 mr-2" /> Download File
            </Button>
          </div>
        ) : blobUrl && isPdf(file) ? (
          <iframe
            src={blobUrl}
            className="w-full h-[450px] border-0"
            title={file.original_filename}
          />
        ) : blobUrl && isImage(file) ? (
          <img src={blobUrl} alt={file.original_filename}
            className="w-full max-h-[400px] object-contain p-4" />
        ) : blobUrl ? (
          <div className="flex flex-col items-center justify-center py-10 text-stone-500 gap-3">
            <FileText className="w-10 h-10 text-stone-300" />
            <p className="text-sm">Preview not available for this file type.</p>
            <Button size="sm" onClick={() => handleDownloadFile(file.id, file.original_filename)}
              className="bg-navy-steal text-white">
              <Download className="w-4 h-4 mr-2" /> Download File
            </Button>
          </div>
        ) : null}
      </div>
    );
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-safety-orange" />
    </div>
  );

  return (
    <>
      <Helmet><title>Overhead Costs - Stone Beam</title></Helmet>

      {/* ── Files Viewer Dialog ─────────────────────────────────────────────── */}
      <Dialog open={viewingFiles} onOpenChange={(open) => { setViewingFiles(open); if (!open) setPreviewFile(null); }}>
        <DialogContent className="sm:max-w-[700px]">
          <DialogHeader>
            <DialogTitle>Attached Files</DialogTitle>
            <DialogDescription>{currentExpenseTitle}</DialogDescription>
          </DialogHeader>

          <div className="space-y-2 max-h-[250px] overflow-y-auto">
            {loadingFiles ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-safety-orange" />
              </div>
            ) : currentExpenseFiles.length === 0 ? (
              <div className="text-center py-8 text-stone-grey">
                <FileText className="w-12 h-12 mx-auto mb-2 text-stone-300" />
                <p>No files attached</p>
              </div>
            ) : currentExpenseFiles.map((file) => {
              const isActive = previewFile?.id === file.id;
              return (
                <div key={file.id} className={cn(
                  'flex items-center justify-between p-3 rounded-lg border transition-colors',
                  isActive ? 'bg-blue-50 border-blue-300' : 'bg-slate-50 hover:bg-slate-100'
                )}>
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <FileText className={cn('w-5 h-5 shrink-0', isActive ? 'text-blue-700' : 'text-blue-600')} />
                    <div className="min-w-0">
                      <p className="font-medium text-sm truncate">{file.original_filename}</p>
                      <p className="text-xs text-stone-500">
                        {(file.filesize / (1024 * 1024)).toFixed(2)} MB &bull; {new Date(file.uploaded_at).toLocaleDateString()}
                        &nbsp;
                        {isPdf(file) && <span className="text-red-500 font-semibold">PDF</span>}
                        {isImage(file) && <span className="text-green-600 font-semibold">Image</span>}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button size="sm" variant="ghost"
                      onClick={() => setPreviewFile(isActive ? null : file)}
                      className={isActive ? 'text-blue-700' : 'text-blue-500 hover:text-blue-700'}>
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost"
                      onClick={() => handleDownloadFile(file.id, file.original_filename)}
                      className="text-stone-500 hover:text-stone-700">
                      <Download className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* ✅ Fixed preview panel */}
          {previewFile && (
            <FilePreviewPanel file={previewFile} onClose={() => setPreviewFile(null)} />
          )}
        </DialogContent>
      </Dialog>

      {/* ── Edit Expense Dialog ─────────────────────────────────────────────── */}
      <Dialog open={editOpen} onOpenChange={(open) => { setEditOpen(open); if (!open) { setEditCurrentFiles([]); setEditFilesToDelete([]); setEditNewFiles([]); } }}>
        <DialogContent className="sm:max-w-[560px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Expense</DialogTitle>
            <DialogDescription>
              Changes will reset status to <strong>Pending</strong> for re-approval.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleEditSubmit} className="space-y-4 mt-2">
            {/* Category */}
            <div className="space-y-1">
              <label className="text-xs font-semibold text-stone-600 uppercase">Category</label>
              <Select value={editCategory} onValueChange={setEditCategory}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* Amount + Date */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-xs font-semibold text-stone-600 uppercase">Amount (AED)</label>
                <Input type="number" step="0.01" placeholder="0.00" value={editAmount}
                  onChange={e => setEditAmount(e.target.value)} required className="font-mono" />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold text-stone-600 uppercase">Date</label>
                <Input type="date" value={editDate} onChange={e => setEditDate(e.target.value)} required />
              </div>
            </div>

            {/* VAT */}
            <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg border">
              <input type="checkbox" id="editVat" checked={editVat} onChange={e => setEditVat(e.target.checked)}
                className="w-4 h-4 text-safety-orange bg-gray-100 border-gray-300 rounded focus:ring-safety-orange" />
              <label htmlFor="editVat" className="text-sm font-medium text-stone-700 cursor-pointer">Add 5% VAT</label>
            </div>

            {/* Amount preview */}
            {editAmount && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm space-y-1">
                <div className="flex justify-between">
                  <span className="text-stone-600">Base Amount:</span>
                  <span className="font-mono font-semibold">{formatCurrency(parseFloat(editAmount) || 0)}</span>
                </div>
                {editVat && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-stone-600">VAT (5%):</span>
                      <span className="font-mono font-semibold text-green-600">+{formatCurrency((parseFloat(editAmount) || 0) * 0.05)}</span>
                    </div>
                    <div className="flex justify-between pt-2 border-t border-blue-300">
                      <span className="font-bold text-navy-steal">Total:</span>
                      <span className="font-mono font-bold text-navy-steal">{formatCurrency(editFinalAmount)}</span>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Title */}
            <div className="space-y-1">
              <label className="text-xs font-semibold text-stone-600 uppercase">Title</label>
              <Input placeholder="e.g. DEWA Bill Oct" value={editTitle} onChange={e => setEditTitle(e.target.value)} required />
            </div>

            {/* Notes */}
            <div className="space-y-1">
              <label className="text-xs font-semibold text-stone-600 uppercase">Notes (optional)</label>
              <Input placeholder="Any additional notes..." value={editNotes} onChange={e => setEditNotes(e.target.value)} />
            </div>

            {/* ── File Management Section ──────────────────────────────────── */}
            <div className="space-y-2 pt-2 border-t">
              <label className="text-xs font-semibold text-stone-600 uppercase">Attached Files</label>

              {/* Currently uploaded files */}
              {editLoadingFiles ? (
                <div className="flex justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-safety-orange" />
                </div>
              ) : editCurrentFiles.length === 0 ? (
                <p className="text-xs text-stone-400 italic">No files attached yet.</p>
              ) : (
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {editCurrentFiles.map((file) => {
                    const markedForDelete = editFilesToDelete.includes(file.id);
                    return (
                      <div key={file.id} className={cn(
                        'flex items-center justify-between p-2 rounded-lg border text-xs transition-all',
                        markedForDelete
                          ? 'bg-red-50 border-red-300 opacity-60'
                          : 'bg-slate-50 border-slate-200'
                      )}>
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <FileText className={cn('w-4 h-4 shrink-0', markedForDelete ? 'text-red-400' : 'text-blue-600')} />
                          <span className={cn('truncate', markedForDelete && 'line-through text-stone-400')}>
                            {file.original_filename}
                          </span>
                          <span className="text-stone-400 shrink-0">
                            ({(file.filesize / (1024 * 1024)).toFixed(2)} MB)
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleMarkFileForDelete(file.id)}
                          title={markedForDelete ? 'Undo delete' : 'Mark for deletion'}
                          className={cn(
                            'shrink-0 ml-2 transition-colors',
                            markedForDelete
                              ? 'text-blue-500 hover:text-blue-700'
                              : 'text-red-400 hover:text-red-600'
                          )}
                        >
                          {markedForDelete ? <Plus className="w-4 h-4" /> : <Trash2 className="w-4 h-4" />}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}

              {editFilesToDelete.length > 0 && (
                <p className="text-xs text-red-500">
                  {editFilesToDelete.length} file(s) will be deleted on save.
                </p>
              )}

              {/* Upload new files */}
              <div className="pt-1">
                <label className="text-xs font-semibold text-stone-500 uppercase">Add New Files</label>
                <FileUpload
                  label="Upload Files"
                  onFilesSelect={(files) => setEditNewFiles(prev => [...prev, ...files])}
                  maxSizeMB={250}
                  multiple={true}
                />
              </div>

              {editNewFiles.length > 0 && (
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {editNewFiles.map((f, i) => (
                    <div key={i} className="flex justify-between items-center text-xs bg-green-50 border border-green-200 p-2 rounded">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <FileText className="w-4 h-4 text-green-600 shrink-0" />
                        <span className="truncate">{f.name}</span>
                        <span className="text-stone-400 shrink-0">({(f.size / (1024 * 1024)).toFixed(2)} MB)</span>
                      </div>
                      <button type="button" onClick={() => handleRemoveNewFile(i)}
                        className="text-red-400 hover:text-red-600 shrink-0 ml-2">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setEditOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={editSubmitting} className="flex-1 bg-navy-steal hover:bg-charcoal text-white">
                {editSubmitting ? (
                  <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" /> Saving...</>
                ) : 'Save Changes'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* ── Main Page ───────────────────────────────────────────────────────── */}
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-navy-steal">Overhead Costs</h1>
            <p className="text-stone-grey">Track and manage company operational expenses</p>
          </div>
          <Button variant="outline" onClick={exportCSV} className="shrink-0">
            <Download className="w-4 h-4 mr-2" /> Export Report
          </Button>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* ── Add Expense Form ── */}
          <div className="lg:col-span-1 space-y-6">
            <Card className="border-t-4 border-t-safety-orange">
              <CardHeader>
                <CardTitle className="text-lg">Add New Expense</CardTitle>
                <CardDescription>Enter cost details and attach receipts</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <label className="text-xs font-semibold text-stone-600 uppercase">Category</label>
                    <Select value={category} onValueChange={setCategory}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <label className="text-xs font-semibold text-stone-600 uppercase">Amount (AED)</label>
                      <Input type="number" step="0.01" placeholder="0.00" value={amount}
                        onChange={e => setAmount(e.target.value)} required className="font-mono" />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-semibold text-stone-600 uppercase">Date</label>
                      <Input type="date" value={date} onChange={e => setDate(e.target.value)} required />
                    </div>
                  </div>

                  <div className="flex items-center space-x-2 p-3 bg-slate-50 rounded-lg border">
                    <input type="checkbox" id="vatIncluded" checked={vatIncluded} onChange={e => setVatIncluded(e.target.checked)}
                      className="w-4 h-4 text-safety-orange bg-gray-100 border-gray-300 rounded focus:ring-safety-orange focus:ring-2" />
                    <label htmlFor="vatIncluded" className="text-sm font-medium text-stone-700 cursor-pointer">Add 5% VAT</label>
                  </div>

                  {amount && (
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-stone-600">Base Amount:</span>
                        <span className="font-mono font-semibold">{formatCurrency(parseFloat(amount) || 0)}</span>
                      </div>
                      {vatIncluded && (
                        <>
                          <div className="flex justify-between items-center text-sm mt-1">
                            <span className="text-stone-600">VAT (5%):</span>
                            <span className="font-mono font-semibold text-green-600">+{formatCurrency((parseFloat(amount) || 0) * 0.05)}</span>
                          </div>
                          <div className="flex justify-between items-center text-sm mt-2 pt-2 border-t border-blue-300">
                            <span className="font-bold text-navy-steal">Total Amount:</span>
                            <span className="font-mono font-bold text-navy-steal text-lg">{formatCurrency(finalAmount)}</span>
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  <div className="space-y-2">
                    <label className="text-xs font-semibold text-stone-600 uppercase">Title</label>
                    <Input placeholder="e.g. DEWA Bill Oct" value={title} onChange={e => setTitle(e.target.value)} required />
                  </div>

                  <div className="space-y-2 pt-2 border-t">
                    <label className="text-xs font-semibold text-stone-600 uppercase">Attach Receipts / Invoices (Max 250MB each)</label>
                    <FileUpload label="Upload Files" onFilesSelect={handleFilesAttached} maxSizeMB={250} multiple={true} />

                    {attachedFiles.length > 0 && (
                      <div className="space-y-2 mt-2 max-h-40 overflow-y-auto">
                        {attachedFiles.map((f, i) => (
                          <div key={i} className="flex justify-between items-center text-xs bg-slate-50 p-2 rounded border">
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <FileText className="w-4 h-4 text-blue-600 shrink-0" />
                              <span className="truncate">{f.name}</span>
                              <span className="text-stone-400 shrink-0">({(f.size / (1024 * 1024)).toFixed(2)} MB)</span>
                            </div>
                            <button type="button" onClick={() => handleRemoveAttachedFile(i)}
                              className="text-red-500 hover:text-red-700 shrink-0 ml-2">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <Button type="submit" disabled={submitting} className="w-full bg-navy-steal hover:bg-charcoal text-white mt-4">
                    {submitting ? (
                      <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />Processing...</>
                    ) : <><Plus className="w-4 h-4 mr-2" /> Record Expense</>}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* ── Main Content ── */}
          <div className="lg:col-span-2">
            <Tabs defaultValue="list" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-4">
                <TabsTrigger value="list">Expenses List</TabsTrigger>
                <TabsTrigger value="analytics">Monthly Report</TabsTrigger>
              </TabsList>

              <TabsContent value="list" className="space-y-4">
                <Card className="bg-navy-steal text-white border-none shadow-md">
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-white/10 rounded-full"><Wallet className="w-5 h-5 text-white" /></div>
                      <div>
                        <p className="text-xs text-stone-300 font-medium uppercase tracking-wider">Total Expenses (Approved)</p>
                        <p className="text-2xl font-bold font-mono">{formatCurrency(filteredTotal)}</p>
                      </div>
                    </div>
                    <div className="text-right text-xs text-stone-400">Based on current filters</div>
                  </CardContent>
                </Card>

                <div className="flex flex-col sm:flex-row gap-4 bg-white p-3 rounded-lg border shadow-sm">
                  <div className="flex-1 flex items-center gap-2">
                    <Filter className="w-4 h-4 text-stone-400" />
                    <span className="text-sm font-medium whitespace-nowrap">Filters:</span>
                  </div>
                  <div className="flex gap-2 w-full sm:w-auto">
                    <Select value={filterCategory} onValueChange={setFilterCategory}>
                      <SelectTrigger className="w-full sm:w-[180px] h-9 text-xs"><SelectValue placeholder="All Categories" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="All">All Categories</SelectItem>
                        {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Select value={filterStatus} onValueChange={setFilterStatus}>
                      <SelectTrigger className="w-full sm:w-[150px] h-9 text-xs"><SelectValue placeholder="All Status" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="All">All Status</SelectItem>
                        <SelectItem value="Pending">Pending</SelectItem>
                        <SelectItem value="Approved">Approved</SelectItem>
                        <SelectItem value="Rejected">Rejected</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <Card className="border-stone-200 shadow-sm">
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader className="bg-slate-50">
                        <TableRow>
                          <TableHead className="w-[90px]">Date</TableHead>
                          <TableHead>Details</TableHead>
                          <TableHead className="w-[160px]">Category</TableHead>
                          <TableHead className="w-[100px]">Status</TableHead>
                          <TableHead className="text-center w-[60px]">Files</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                          <TableHead className="text-right w-[160px]">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredExpenses.map((exp) => (
                          <TableRow key={exp.id} className="group hover:bg-slate-50">
                            <TableCell className="text-stone-500 font-medium whitespace-nowrap align-middle">
                              {new Date(exp.date).toLocaleDateString()}
                            </TableCell>
                            <TableCell className="align-middle">
                              <div className="font-semibold text-navy-steal">{exp.description || exp.title}</div>
                              {exp.vat_included == 1 && (
                                <span className="text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">+5% VAT</span>
                              )}
                            </TableCell>
                            <TableCell className="align-middle text-stone-600">{exp.category}</TableCell>
                            <TableCell className="align-middle">{getStatusBadge(exp.status || 'Pending')}</TableCell>
                            <TableCell className="text-center align-middle">
                              {exp.file_count > 0 ? (
                                <Button variant="ghost" size="icon" className="h-7 w-7 text-blue-600 hover:text-blue-800"
                                  onClick={() => handleViewFiles(exp.id, exp.description || exp.title)}
                                  title={`${exp.file_count} file${exp.file_count > 1 ? 's' : ''} attached`}>
                                  <Eye className="w-4 h-4" />
                                </Button>
                              ) : <span className="text-xs text-muted-foreground">-</span>}
                            </TableCell>
                            <TableCell className="text-right font-bold text-navy-steal font-mono align-middle">
                              {formatCurrency(exp.amount)}
                            </TableCell>
                            <TableCell className="text-right align-middle">
                              <div className="flex justify-end gap-1 flex-wrap">
                                <Button size="icon" variant="ghost"
                                  className="h-8 w-8 text-blue-500 hover:bg-blue-50 hover:text-blue-700"
                                  onClick={() => handleOpenEdit(exp)} title="Edit">
                                  <Pencil className="w-4 h-4" />
                                </Button>

                                {(exp.status === 'Pending' || !exp.status) && isAdmin && (
                                  <>
                                    <Button size="icon" variant="ghost"
                                      className="h-8 w-8 text-green-600 hover:bg-green-50 hover:text-green-700"
                                      onClick={() => handleApprove(exp.id)} title="Approve">
                                      <Check className="w-5 h-5" />
                                    </Button>
                                    <Button size="icon" variant="ghost"
                                      className="h-8 w-8 text-red-500 hover:bg-red-50 hover:text-red-600"
                                      onClick={() => handleReject(exp.id)} title="Reject">
                                      <X className="w-5 h-5" />
                                    </Button>
                                  </>
                                )}

                                <Button size="icon" variant="ghost"
                                  className="h-8 w-8 text-stone-400 hover:bg-red-50 hover:text-red-600"
                                  onClick={() => (exp.status === 'Pending' || !exp.status) ? handleCancel(exp.id) : handleDelete(exp.id)}
                                  title="Delete">
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                        {filteredExpenses.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={7} className="text-center py-12 text-stone-grey">
                              <div className="flex flex-col items-center justify-center opacity-50">
                                <Search className="w-8 h-8 mb-2 text-stone-300" />
                                <p>No expenses found</p>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* ── Analytics Tab ── */}
              <TabsContent value="analytics" className="space-y-6">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <h2 className="text-xl font-bold text-navy-steal">
                    Monthly Report - {analyticsData.currentYear}
                    {selectedMonth !== 'All' && ` - ${monthOptions.find(m => m.value === selectedMonth)?.label}`}
                  </h2>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-stone-500 whitespace-nowrap">Year:</span>
                      <Select value={selectedYear} onValueChange={setSelectedYear}>
                        <SelectTrigger className="w-[120px]"><SelectValue /></SelectTrigger>
                        <SelectContent>{availableYears.map(y => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-stone-500 whitespace-nowrap">Month:</span>
                      <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                        <SelectTrigger className="w-[150px]"><SelectValue /></SelectTrigger>
                        <SelectContent>{monthOptions.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {selectedMonth !== 'All' && analyticsData.selectedMonthData && (
                  <Card className="bg-blue-50 border-blue-200">
                    <CardContent className="p-6">
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="text-sm font-medium text-blue-700">{monthOptions.find(m => m.value === selectedMonth)?.label} {analyticsData.currentYear}</p>
                          <p className="text-3xl font-bold text-navy-steal mt-2">{formatCurrency(analyticsData.selectedMonthData.total)}</p>
                          <p className="text-xs text-stone-600 mt-1">{analyticsData.selectedMonthData.count} expense{analyticsData.selectedMonthData.count !== 1 ? 's' : ''}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-stone-600">% of Year Total</p>
                          <p className="text-2xl font-bold text-blue-600">
                            {analyticsData.yearTotal > 0 ? ((analyticsData.selectedMonthData.total / analyticsData.yearTotal) * 100).toFixed(1) : 0}%
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {selectedMonth === 'All' && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card className="bg-slate-50 border-slate-200"><CardContent className="p-4">
                      <p className="text-[10px] uppercase font-bold text-stone-500">Year Total ({analyticsData.currentYear})</p>
                      <p className="text-lg font-bold text-navy-steal mt-1">{formatCurrency(analyticsData.yearTotal)}</p>
                      <p className="text-[10px] text-green-600 font-medium">Approved Only</p>
                    </CardContent></Card>
                    <Card className="bg-slate-50 border-slate-200"><CardContent className="p-4">
                      <p className="text-[10px] uppercase font-bold text-stone-500">Monthly Average</p>
                      <p className="text-lg font-bold text-navy-steal mt-1">{formatCurrency(analyticsData.avgMonthly)}</p>
                    </CardContent></Card>
                    <Card className="bg-green-50 border-green-200"><CardContent className="p-4">
                      <p className="text-[10px] uppercase font-bold text-green-700 flex items-center gap-1"><TrendingUp className="w-3 h-3" /> Highest Month</p>
                      <p className="text-lg font-bold text-green-800 mt-1">{analyticsData.highestMonth?.name || '-'}</p>
                      <p className="text-xs text-green-600">{formatCurrency(analyticsData.highestMonth?.total || 0)}</p>
                    </CardContent></Card>
                    <Card className="bg-blue-50 border-blue-200"><CardContent className="p-4">
                      <p className="text-[10px] uppercase font-bold text-blue-700 flex items-center gap-1"><TrendingDown className="w-3 h-3" /> Lowest Month</p>
                      <p className="text-lg font-bold text-blue-800 mt-1">{analyticsData.lowestMonth?.name || '-'}</p>
                      <p className="text-xs text-blue-600">{formatCurrency(analyticsData.lowestMonth?.total || 0)}</p>
                    </CardContent></Card>
                  </div>
                )}

                <Card>
                  <CardHeader>
                    <CardTitle>{selectedMonth === 'All' ? 'Monthly Expense Breakdown' : `${monthOptions.find(m => m.value === selectedMonth)?.label} Details`}</CardTitle>
                    <CardDescription>Visual comparison of <strong>APPROVED</strong> expenses for {analyticsData.currentYear}{selectedMonth !== 'All' && ` - ${monthOptions.find(m => m.value === selectedMonth)?.label}`}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="h-[300px] w-full mt-4">
                      <div className={cn('flex h-full items-end gap-2 md:gap-4 px-2', selectedMonth !== 'All' && 'justify-center')}>
                        {analyticsData.monthlyData.map((data, idx) => {
                          const heightPercent     = data.total > 0 ? (data.total / analyticsData.maxTotal) * 100 : 0;
                          const percentageOfTotal = analyticsData.yearTotal > 0 ? ((data.total / analyticsData.yearTotal) * 100).toFixed(1) : 0;
                          return (
                            <div key={idx} className={cn('flex flex-col items-center justify-end group h-full relative', selectedMonth === 'All' ? 'flex-1' : 'w-32')}>
                              <div className="opacity-0 group-hover:opacity-100 absolute bottom-full mb-2 bg-navy-steal text-white text-xs p-2 rounded pointer-events-none transition-opacity z-10 w-24 text-center">
                                <div className="font-bold">{data.name}</div>
                                <div>{formatCurrency(data.total)}</div>
                                <div className="text-stone-300">{percentageOfTotal}% of Year</div>
                                <div className="text-stone-300">{data.count} expenses</div>
                              </div>
                              <div className={cn('w-full rounded-t-sm transition-all duration-500 ease-out', data.total > 0 ? 'bg-safety-orange hover:bg-orange-600 min-h-[4px]' : 'bg-slate-100 h-[2px]')}
                                style={{ height: `${heightPercent}%` }} />
                              <span className="text-[10px] font-medium text-stone-500 mt-2 truncate w-full text-center">{data.name}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </>
  );
};

export default OverheadPage;